import { Injectable } from '@angular/core';
import { pick } from 'lodash';
import { firstValueFrom, map, Subject } from 'rxjs';

import { HttpClient, HttpParams } from '@angular/common/http';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { ExceptionLoggingService } from 'src/app/components/logging/exception-logging.service';
import { ApiListResponse, ApiResponse } from '../api';
import { ApiSearch } from './api-utils.service';
import { ApiService } from './api.service';
import { Display, DisplaySummary } from './display';
import { FoldersApiService } from './folders-api.service';

@Injectable({
  providedIn: 'root',
})
export class DisplayApiService extends ApiService<Display> {
  static readonly DISPLAY_WRITABLE_FIELDS = [
    'name',
    'status',
    'useCompanyAddress',
    'addressDescription',
    'street',
    'unit',
    'city',
    'province',
    'country',
    'postalCode',
    'timeZoneOffset',
    'restartEnabled',
    'restartTime',
    'autostartOnBoot',
    'width',
    'height',
    'orientation',
    'monitoringEnabled',
    'monitoringEmails',
    'monitoringSchedule',
    'activationKey',
    'overrideTakeoverAllowed',
    'displayControl',
    'favoredBy',
    'screenSharingEnabled',
    'settings',
    'folderId',
  ];

  displaysLoadedSubject: Subject<Array<any>> = new Subject();

  constructor(
    protected httpClient: HttpClient,
    protected companyStateService: CompanyStateService,
    private exceptionLoggingService: ExceptionLoggingService,
  ) {
    super(httpClient);
  }

  override getResourceUrl(): string {
    return '/v2/displays';
  }

  override toServerModel(entity: Display, isNew: boolean): any {
    const fields = pick(entity, DisplayApiService.DISPLAY_WRITABLE_FIELDS);

    if (isNew) {
      fields.assignLicense = entity.playerProAuthorized;
    }

    // TODO: It should be handled by the server
    if (fields.folderId === undefined || fields.folderId === null) {
      delete fields.folderId;
    }

    return fields;
  }

  list(search: ApiSearch, cursor?: string): Promise<ApiListResponse<Display>> {
    let params = new HttpParams()
      .set('companyId', this.companyStateService.getSelectedCompanyId())
      .set('search', search.query || '*')
      .set('filter', this.buildSearchFilter(search))
      .set('includeSubcompanies', search.includeSubcompanies || false)
      .set('count', search.count)
      .set('sort', search.sortBy + (search.reverse ? ' desc' : ' asc'));

    if (cursor) {
      params = params.set('cursor', cursor);
    }

    return firstValueFrom(
      super.listAsObservable(params).pipe(
        map((resp) => {
          try {
            if (resp.items) {
              for (const item of resp.items) {
                item.lastActivityDate =
                  item.onlineStatus === 'online'
                    ? new Date()
                    : item.lastActivityDate
                      ? new Date(item.lastActivityDate)
                      : '';
              }
            }

            this.displaysLoadedSubject.next(resp.items);
          } catch (e) {
            this.exceptionLoggingService.handle(
              e,
              'Failed process displays.',
              true,
            );
          }

          return resp;
        }),
      ),
    );
  }

  private buildSearchFilter(search: ApiSearch): string {
    if (Object.hasOwn(search, 'folderId')) {
      return [
        !search.query
          ? `folderId:${search.folderId || FoldersApiService.ROOT_FOLDER_ID}`
          : search.folderId
            ? `folderHierarchy:${search.folderId}`
            : '',
        search.filter ? search.filter : '',
      ]
        .filter(Boolean)
        .join('&&');
    }
    return search.filter ? search.filter : '';
  }

  get(displayId: string): Promise<ApiResponse<Display>> {
    return firstValueFrom(super.getAsObservable(displayId)).then((resp) => {
      const item = resp.item;

      if (item) {
        item.lastActivityDate =
          item.onlineStatus === 'online'
            ? new Date()
            : item.lastActivityDate
              ? new Date(item.lastActivityDate)
              : '';

        if (item.overrideTakeoverAllowed === undefined) {
          item.overrideTakeoverAllowed = true;
        }

        item.settings = item.settings || {};

        item.autostartOnBoot =
          item.autostartOnBoot === undefined ? true : item.autostartOnBoot;

        if (item.settings && item.settings.setAsScreenSaver) {
          item.settings.setAsScreenSaver =
            item.settings.setAsScreenSaver === 'true';
        }

        if (
          item.settings &&
          item.settings.volumeControlSupported !== undefined
        ) {
          if (typeof item.settings.volumeControlSupported === 'string') {
            item.settings.volumeControlSupported =
              item.settings.volumeControlSupported === 'true';
          }
          // Leave boolean values untouched
        }

        if (item.settings && item.settings.volumePercentage !== undefined) {
          if (typeof item.settings.volumePercentage === 'string') {
            item.settings.volumePercentage = parseInt(
              item.settings.volumePercentage,
              10,
            );
          }
          // Leave numeric values untouched
        }

        this.displaysLoadedSubject.next([item]);
      }

      return resp;
    });
  }

  add(display: any): Promise<ApiResponse<Display>> {
    return firstValueFrom(
      super.addAsObservable(
        this.companyStateService.getSelectedCompanyId(),
        display,
      ),
    );
  }

  update(displayId: string, display: any): Promise<ApiResponse<Display>> {
    return firstValueFrom(super.updateAsObservable(displayId, display));
  }

  updateAccessControlList(
    displayId: string,
    accessControlList: NonNullable<Display['accessControlList']>,
    comment?: string,
  ): Promise<ApiResponse<Display>> {
    const data =
      comment === undefined
        ? { accessControlList }
        : { accessControlList, comment };

    return firstValueFrom(
      this.httpClient.patch<ApiResponse<Display>>(
        `${this.APIUrl}/${displayId}`,
        data,
      ),
    );
  }

  getAccessControlList(
    displayId: string,
  ): Promise<NonNullable<Display['accessControlList']>> {
    return firstValueFrom(
      this.httpClient.get<ApiResponse<Display>>(`${this.APIUrl}/${displayId}`),
    ).then((response) => response.item?.accessControlList ?? {});
  }

  delete(displayId: string): Promise<ApiResponse<Display>> {
    return firstValueFrom(super.deleteAsObservable(displayId));
  }

  restart(displayId: string): Promise<ApiResponse<Display>> {
    return firstValueFrom(
      super.requestAsObservable(displayId, 'PATCH', 'restart'),
    );
  }

  reboot(displayId: string): Promise<ApiResponse<Display>> {
    return firstValueFrom(
      super.requestAsObservable(displayId, 'PATCH', 'reboot'),
    );
  }

  updateFirmware(
    displayId: string,
    firmwareUrl?: string,
  ): Promise<ApiResponse<Display>> {
    // Legacy method - kept for backward compatibility
    // For new implementations, use requestFirmwareUpdate with clientId
    const body = firmwareUrl ? { firmwareUrl } : {};
    return firstValueFrom(
      super.requestAsObservable(
        displayId,
        'PATCH',
        'update-firmware',
        undefined,
        body,
      ),
    );
  }

  requestFirmwareUpdate(
    displayId: string,
    clientId: string,
    firmwareUrl: string,
  ): Promise<ApiResponse<Display>> {
    /**
     * Request firmware update for LG webOS Player or Amazon Signage Stick display.
     *
     * Any valid URL that ends with .epk is accepted; the firmware file is sent
     * to the device for upgrade (e.g. from Rise Vision Storage or another host).
     *
     * Backend endpoint: PATCH /api/v1/displays/:displayId/request-firmware-update
     * Query params: clientId, firmwareUrl
     *
     * Flow:
     * 1. Backend receives request with displayId, clientId, and firmwareUrl
     * 2. Validates the firmware URL (must end with .epk)
     * 3. Validates display is online
     * 4. Sends 'firmware-upgrade-request' message to Viewer via Core/Primus
     *    Message format: { msg: "firmware-upgrade-request", url: firmwareUrl, clientId: clientId }
     * 5. Viewer calls player.requestFirmwareUpgrade(url, callback)
     * 6. Player invokes Luna service (LG webOS): luna://.../scapservice/firmwareUpgrade
     * 7. Status updates flow back: firmware-upgrade-started, firmware-upgrade-complete, firmware-upgrade-failed
     *
     * The clientId is generated by Core when the Primus connection is established
     * and is used to route status messages back to the correct UI connection.
     */
    const params = new HttpParams()
      .set('clientId', clientId)
      .set('firmwareUrl', firmwareUrl);

    return firstValueFrom(
      super.requestAsObservable(
        displayId,
        'PATCH',
        'request-firmware-update',
        params,
      ),
    );
  }

  requestScreenshot(displayId, clientId) {
    const params = new HttpParams().set('clientId', clientId);

    return firstValueFrom(
      super.requestAsObservable(
        displayId,
        'PATCH',
        'request-screenshot',
        params,
      ),
    );
  }

  hasFreeDisplays(
    companyId: string | null,
    displayIds: string[],
  ): Promise<ApiResponse<string>> {
    const params = new HttpParams().set('companyId', companyId);
    const body = {
      displayIds: displayIds,
    };

    return firstValueFrom(
      super.requestAsObservable(
        null,
        'POST',
        'has-free-displays',
        params,
        body,
      ),
    );
  }

  summary(
    companyId?: string | null,
    includeSubcompanies: boolean = false,
  ): Promise<DisplaySummary> {
    companyId = companyId || this.companyStateService.getSelectedCompanyId();

    const params = new HttpParams()
      .set('companyId', companyId)
      .set('includeSubcompanies', includeSubcompanies);

    return firstValueFrom(
      super.requestAsObservable(null, 'GET', 'summary', params),
    );
  }

  export(): Promise<ApiResponse<string>> {
    const companyId = this.companyStateService.getSelectedCompanyId();
    const params = new HttpParams().set('companyId', companyId);

    return firstValueFrom(
      super.requestAsObservable(null, 'GET', 'export', params),
    );
  }

  move(displayId: string, companyId: string): Promise<ApiResponse<Display>> {
    const params = new HttpParams().set('companyId', companyId);

    return firstValueFrom(
      super.requestAsObservable(displayId, 'PATCH', 'move', params),
    );
  }

  sendNotification(
    displayId: string,
    type: string,
  ): Promise<ApiResponse<Display>> {
    const params = new HttpParams().set('type', type);

    return firstValueFrom(
      super.requestAsObservable(displayId, 'PATCH', 'notification', params),
    );
  }
}
